import React, { Suspense, lazy, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ConsoleAuthProvider, useConsoleAuth } from "@/contexts/ConsoleAuthContext";
import LockScreen from "@/components/LockScreen";
import axios from "axios";
import { attachDeviceHeader } from "@/utils/deviceId";
attachDeviceHeader(axios);

// Always clear unlock flag on page load/refresh — forces PIN re-entry after every refresh
sessionStorage.removeItem("bompay_unlocked");

// Direct imports for critical entry-point pages (no lazy loading)
import ConsoleLogin from "@/pages/console/ConsoleLogin";

// Public website pages (lazy-loaded)
const HomePage = lazy(() => import("@/pages/website/HomePage"));
const AboutPage = lazy(() => import("@/pages/website/AboutPage"));
const ServicesPage = lazy(() => import("@/pages/website/ServicesPage"));
const ProductsPage = lazy(() => import("@/pages/website/ProductsPage"));
const CareersPage = lazy(() => import("@/pages/website/CareersPage"));
const BlogPage = lazy(() => import("@/pages/website/BlogPage"));
const BlogDetailPage = lazy(() => import("@/pages/website/BlogDetailPage"));
const ContactPage = lazy(() => import("@/pages/website/ContactPage"));
const TermsPage = lazy(() => import("@/pages/website/TermsPage"));
const PrivacyPage = lazy(() => import("@/pages/website/PrivacyPage"));
const CookiesPage = lazy(() => import("@/pages/website/CookiesPage"));

// Mobile app entry
const AuthPage = lazy(() => import("@/pages/AuthPage"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const AddMoney = lazy(() => import("@/pages/AddMoney"));
const SendMoney = lazy(() => import("@/pages/SendMoney"));
const Services = lazy(() => import("@/pages/Services"));
const Savings = lazy(() => import("@/pages/Savings"));
const Loans = lazy(() => import("@/pages/Loans"));
const Rewards = lazy(() => import("@/pages/Rewards"));
const EposPage = lazy(() => import("@/pages/EposPage"));
const CardsPage = lazy(() => import("@/pages/CardsPage"));
const BusinessPage = lazy(() => import("@/pages/BusinessPage"));
const BusinessCreatePage = lazy(() => import("@/pages/BusinessCreatePage"));
const BusinessDashboard = lazy(() => import("@/pages/BusinessDashboard"));
const FamilyPage = lazy(() => import("@/pages/FamilyPage"));
const FamilyFundPage = lazy(() => import("@/pages/FamilyFundPage"));
const FamilyWithdrawPage = lazy(() => import("@/pages/FamilyWithdrawPage"));
const FamilyAddMemberPage = lazy(() => import("@/pages/FamilyAddMemberPage"));
const FamilyActivityPage = lazy(() => import("@/pages/FamilyActivityPage"));
const FamilyMemberPage = lazy(() => import("@/pages/FamilyMemberPage"));
const FamilyRequestsPage = lazy(() => import("@/pages/FamilyRequestsPage"));
const AllServicesPage = lazy(() => import("@/pages/AllServicesPage"));
const Transactions = lazy(() => import("@/pages/Transactions"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const Profile = lazy(() => import("@/pages/Profile"));
const ConsoleLayout = lazy(() => import("@/pages/console/ConsoleLayout"));
const ConsoleDashboard = lazy(() => import("@/pages/console/ConsoleDashboard"));
const ConsoleUsers = lazy(() => import("@/pages/console/ConsoleUsers"));
const ConsoleTransactions = lazy(() => import("@/pages/console/ConsoleTransactions"));
const ConsoleKYC = lazy(() => import("@/pages/console/ConsoleKYC"));
const ConsoleFraud = lazy(() => import("@/pages/console/ConsoleFraud"));
const ConsoleLoans = lazy(() => import("@/pages/console/ConsoleLoans"));
const ConsoleSavings = lazy(() => import("@/pages/console/ConsoleSavings"));
const ConsoleNotifications = lazy(() => import("@/pages/console/ConsoleNotifications"));
const ConsoleProviders = lazy(() => import("@/pages/console/ConsoleProviders"));
const ConsoleServiceAccounts = lazy(() => import("@/pages/console/ConsoleServiceAccounts"));
const ConsoleRoles = lazy(() => import("@/pages/console/ConsoleRoles"));
const ConsoleAudit = lazy(() => import("@/pages/console/ConsoleAudit"));
const ConsoleSupport = lazy(() => import("@/pages/console/ConsoleSupport"));
const ConsoleLedger = lazy(() => import("@/pages/console/ConsoleLedger"));
const ConsoleFees = lazy(() => import("@/pages/console/ConsoleFees"));
const ConsoleMoneyMovement = lazy(() => import("@/pages/console/ConsoleMoneyMovement"));
const ConsoleChargeAccounts = lazy(() => import("@/pages/console/ConsoleChargeAccounts"));
const ConsoleBalanceAccounts = lazy(() => import("@/pages/console/ConsoleBalanceAccounts"));
const ConsolePromotions = lazy(() => import("@/pages/console/ConsolePromotions"));
const ConsoleAjo = lazy(() => import("@/pages/console/ConsoleAjo"));
const ConsoleSmsConfig = lazy(() => import("@/pages/console/ConsoleSmsConfig"));
const AppMobilePage = lazy(() => import("@/pages/AppMobilePage"));
const ConsoleFamily = lazy(() => import("@/pages/console/ConsoleFamily"));
const ConsoleUseAndEarn = lazy(() => import("@/pages/console/ConsoleUseAndEarn"));
const ConsoleEpos = lazy(() => import("@/pages/console/ConsoleEpos"));
const ConsoleBlog = lazy(() => import("@/pages/console/ConsoleBlog"));
const ConsoleFeeRevenue = lazy(() => import("@/pages/console/ConsoleFeeRevenue"));
const ConsoleBusiness = lazy(() => import("@/pages/console/ConsoleBusiness"));

const KYCSetup = lazy(() => import("@/pages/KYCSetup"));
const KYCUpgrade = lazy(() => import("@/pages/KYCUpgrade"));
const BompayTransfer = lazy(() => import("@/pages/BompayTransfer"));
const AirtimePage = lazy(() => import("@/pages/AirtimePage"));
const DataBundlesPage = lazy(() => import("@/pages/DataBundlesPage"));
const ElectricityPage = lazy(() => import("@/pages/ElectricityPage"));
const CableTVPage = lazy(() => import("@/pages/CableTVPage"));
const BettingPage = lazy(() => import("@/pages/BettingPage"));
const AjoPage = lazy(() => import("@/pages/AjoPage"));
const Referrals = lazy(() => import("@/pages/Referrals"));

const SplashScreen = () => (
    <div className="min-h-screen bg-[#064BCB] flex flex-col items-center justify-center">
    <div className="relative mb-6">
      <div className="w-24 h-24 rounded-full border-4 border-white/20 border-t-white animate-spin" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center shadow-lg overflow-hidden p-1">
          <img
            src="https://customer-assets-eiarnc6j.emergentagent.net/job_bompay-ledger/artifacts/q64ulo6z_Fav.png"
            alt="Bompay"
            className="w-full h-full object-contain"
          />
        </div>
      </div>
    </div>
    <img
      src="https://customer-assets-eiarnc6j.emergentagent.net/job_bompay-ledger/artifacts/v92480gb_BomPay.png"
      alt="Bompay"
      className="h-8 w-auto object-contain brightness-0 invert mb-1"
    />
    {/* <p className="text-white/50 text-xs mt-1">Loading your wallet...</p> */}
  </div>
);

const PageLoader = () => (
  <div className="min-h-screen bg-[#FAFAFC] flex items-center justify-center">
    <div className="relative">
      <div className="w-16 h-16 rounded-full border-4 border-[#064BCB]/20 border-t-[#064BCB] animate-spin" />
      <div className="absolute inset-0 flex items-center justify-center">
        <img
          src="https://customer-assets-eiarnc6j.emergentagent.net/job_bompay-ledger/artifacts/q64ulo6z_Fav.png"
          alt="Bompay"
          className="w-8 h-8 object-contain"
        />
      </div>
    </div>
  </div>
);

const AppRoutes = () => {
  const { user, loading: authLoading } = useAuth();
  const { admin, loading: consoleLoading } = useConsoleAuth();
  const location = useLocation();
  const [isLocked, setIsLocked] = useState(false);
  const isConsole = location.pathname.startsWith("/console");
  const isApp = location.pathname.startsWith("/appmobile");

  // Lock check: when user is set (from existing session via checkAuth), check if we should lock.
  // On fresh login/register, AuthContext sets bompay_unlocked, so no lock shown.
  // On tab-close+reopen, sessionStorage is cleared → lock shown.
  useEffect(() => {
    if (user && isApp) {
      if (!sessionStorage.getItem("bompay_unlocked")) setIsLocked(true);
    } else if (!user) {
      setIsLocked(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, isApp]);

  // Auto-lock after 60 seconds of inactivity while app is open
  useEffect(() => {
    if (!user || !isApp || isLocked) return;
    let inactivityTimer;
    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        sessionStorage.removeItem("bompay_unlocked");
        setIsLocked(true);
      }, 120 * 60 * 1000); // 120 minutes
    };
    const events = ["mousemove", "mousedown", "touchstart", "keydown", "scroll", "click"];
    events.forEach(e => document.addEventListener(e, resetTimer, { passive: true }));
    resetTimer(); // start timer immediately
    return () => {
      clearTimeout(inactivityTimer);
      events.forEach(e => document.removeEventListener(e, resetTimer));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, isApp, isLocked]);

  // Lock when app goes to background (tab hidden / app switch)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && user && isApp) {
        sessionStorage.removeItem("bompay_unlocked");
        setIsLocked(true);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isApp]);

  // Only show the app splash screen for app/console routes — website pages render immediately
  const isAppOrConsole = location.pathname.startsWith("/appmobile") || location.pathname.startsWith("/console");
  if ((authLoading || consoleLoading) && isAppOrConsole) return <SplashScreen />;

  return (
    <>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* ── CONSOLE ROUTES ── nested structure avoids /console vs /console/* conflict */}
        <Route path="/console">
          <Route index element={admin ? <Navigate to="/console/dashboard" replace /> : <ConsoleLogin />} />
          <Route element={admin ? <ConsoleLayout /> : <Navigate to="/console" replace />}>
            <Route path="dashboard" element={<ConsoleDashboard />} />
            <Route path="users" element={<ConsoleUsers />} />
            <Route path="transactions" element={<ConsoleTransactions />} />
            <Route path="kyc" element={<ConsoleKYC />} />
            <Route path="fraud" element={<ConsoleFraud />} />
            <Route path="loans" element={<ConsoleLoans />} />
            <Route path="savings" element={<ConsoleSavings />} />
            <Route path="notifications" element={<ConsoleNotifications />} />
            <Route path="providers" element={<ConsoleProviders />} />
            <Route path="service-accounts" element={<ConsoleServiceAccounts />} />
            <Route path="roles" element={<ConsoleRoles />} />
            <Route path="audit" element={<ConsoleAudit />} />
            <Route path="support" element={<ConsoleSupport />} />
            <Route path="ledger" element={<ConsoleLedger />} />
            <Route path="fees" element={<ConsoleFees />} />
            <Route path="money-movement" element={<ConsoleMoneyMovement />} />
            <Route path="charge-accounts" element={<ConsoleChargeAccounts />} />
            <Route path="balance-accounts" element={<ConsoleBalanceAccounts />} />
            <Route path="sms-config" element={<ConsoleSmsConfig />} />
            <Route path="promotions" element={<ConsolePromotions />} />
            <Route path="fee-revenue" element={<ConsoleFeeRevenue />} />
            <Route path="ajo" element={<ConsoleAjo />} />
            <Route path="family" element={<ConsoleFamily />} />
            <Route path="use-and-earn" element={<ConsoleUseAndEarn />} />
            <Route path="epos" element={<ConsoleEpos />} />
            <Route path="blogs" element={<ConsoleBlog />} />
            <Route path="business" element={<ConsoleBusiness />} />
          </Route>
        </Route>

        {/* ── PUBLIC WEBSITE ROUTES ── always accessible */}
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogDetailPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/cookies" element={<CookiesPage />} />

        {/* ── MOBILE APP ENTRY ── */}
        <Route path="/appmobile" element={user ? <Navigate to="/appmobile/dashboard" replace /> : <AppMobilePage />} />

        {/* ── MOBILE APP SUB-ROUTES (all under /appmobile/) ── */}
        <Route path="/appmobile/auth" element={user ? <Navigate to="/appmobile/dashboard" replace /> : <AuthPage />} />
        <Route path="/appmobile/dashboard" element={user ? <Dashboard /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/add-money" element={user ? <AddMoney /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/send" element={user ? <SendMoney /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/kyc-setup" element={user ? <KYCSetup /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/kyc" element={user ? <KYCUpgrade /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/bompay-send" element={user ? <BompayTransfer /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/airtime" element={user ? <AirtimePage /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/data" element={user ? <DataBundlesPage /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/electricity" element={user ? <ElectricityPage /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/cable-tv" element={user ? <CableTVPage /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/betting" element={user ? <BettingPage /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/vas" element={user ? <Services /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/savings" element={user ? <Savings /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/loans" element={user ? <Loans /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/ajo" element={user ? <AjoPage /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/rewards" element={user ? <Rewards /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/referrals" element={user ? <Referrals /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/epos" element={user ? <EposPage /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/cards" element={user ? <CardsPage /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/business" element={user ? <BusinessPage /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/business/create" element={user ? <BusinessCreatePage /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/business/:bizId" element={user ? <BusinessDashboard /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/family" element={user ? <FamilyPage /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/family/:familyId/fund" element={user ? <FamilyFundPage /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/family/:familyId/withdraw" element={user ? <FamilyWithdrawPage /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/family/:familyId/members/add" element={user ? <FamilyAddMemberPage /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/family/:familyId/activity" element={user ? <FamilyActivityPage /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/family/:familyId/requests" element={user ? <FamilyRequestsPage /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/family/:familyId/members/:memberId" element={user ? <FamilyMemberPage /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/appservices" element={user ? <AllServicesPage /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/transactions" element={user ? <Transactions /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/notifications" element={user ? <Notifications /> : <Navigate to="/appmobile" replace />} />
        <Route path="/appmobile/profile" element={user ? <Profile /> : <Navigate to="/appmobile" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
    {isLocked && user && isApp && (
      <LockScreen onUnlock={() => { setIsLocked(false); sessionStorage.setItem("bompay_unlocked", "1"); }} />
    )}
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ConsoleAuthProvider>
        <AuthProvider>
          <Toaster
            position="top-center"
            toastOptions={{
              style: {
                background: "#FFFFFF",
                color: "#0F172A",
                border: "1px solid #E2E8F0",
                borderRadius: "12px",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: "14px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
              },
            }}
          />
          <AppRoutes />
        </AuthProvider>
      </ConsoleAuthProvider>
    </BrowserRouter>
  );
}

export default App;
